import type { CompareProduct } from '@iheartjane/menu';
import { useCompare } from '@iheartjane/menu';

import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import type { CompareProductPayload } from '@jane/bloom/viewModels/compareProductPayload';
import { isCompareProductPayload } from '@jane/bloom/viewModels/compareProductPayload';

/** Identifies the compare modal as the origin of an add-to-cart fetcher. */
export const COMPARE_ROW_ID = 'compare';

export interface ProductAddedToBag {
  payload: CompareProductPayload;
  productId: number;
}

/**
 * Renders nothing. Subscribes to a compared product's add-to-cart fetcher via `useAddToCartConfig`.
 */
const CompareAddTracker = ({ payload, productId }: ProductAddedToBag) => {
  useAddToCartConfig({
    cardLocation: 'compare',
    product: payload.trackingProduct,
    productId,
    quantity: 1,
    rowId: COMPARE_ROW_ID,
    selectedWeight: payload.defaultWeight,
  });

  return null;
};

/** Products whose payload is unusable have nothing to report and are skipped. */
const trackable = (product: CompareProduct): ProductAddedToBag[] =>
  isCompareProductPayload(product.payload)
    ? [{ payload: product.payload, productId: product.id }]
    : [];

interface CompareAddTrackersProps {
  productsAddedToBag: ProductAddedToBag[];
}

/**
 * Mounts one tracker per compared product, plus one for every product an add-to-bag
 * has already been fired for.
 */
export const CompareAddTrackers = ({
  productsAddedToBag,
}: CompareAddTrackersProps) => {
  const { products } = useCompare();

  // `products` is what is still in the comparison,
  // `productsAddedToBag` is what has been added to bag from the Compare modal.
  //
  // Being compared is what mounts a tracker *before* its request to add to bag is submitted,
  // which makes the fetcher observable.
  // Having been added to bag is what keeps that same tracker mounted afterwards, so
  // removing the product from comparison right after adding it to bag can't leave the request
  // settling with nothing left to report it.
  const tracked = new Map<number, CompareProductPayload>();
  for (const { payload, productId } of productsAddedToBag) {
    tracked.set(productId, payload);
  }
  for (const { payload, productId } of products.flatMap(trackable)) {
    tracked.set(productId, payload);
  }

  return (
    <>
      {[...tracked].map(([productId, payload]) => (
        <CompareAddTracker
          key={productId}
          payload={payload}
          productId={productId}
        />
      ))}
    </>
  );
};
